// =============================================
// useSchemaReadiness — dry-run check czy schema.org jest gotowa dla publish.
// 4 stany: ready (zielony) / partial (amber) / off (szary) / invalid (czerwony)
// =============================================

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type SchemaReadinessLevel = "ready" | "partial" | "off" | "invalid" | "loading";

export interface SchemaReadiness {
  level: SchemaReadinessLevel;
  label: string;
  color: string;
  reasons: string[];
}

const LEVELS: Record<SchemaReadinessLevel, { label: string; color: string }> = {
  ready: { label: "Schema ready", color: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30" },
  partial: { label: "Schema partial", color: "text-amber-500 bg-amber-500/10 border-amber-500/30" },
  off: { label: "Schema off", color: "text-muted-foreground bg-muted/30 border-border" },
  invalid: { label: "Schema invalid", color: "text-red-500 bg-red-500/10 border-red-500/30" },
  loading: { label: "...", color: "text-muted-foreground" },
};

/**
 * Dry-run readiness check — tylko DB reads, zero LLM call, zero side effects.
 * Używany per node w ContentEnginePanel do pokazania badge przed publish.
 */
export function useSchemaReadiness(nodeId: string | null) {
  return useQuery<SchemaReadiness>({
    queryKey: ["schemaReadiness", nodeId],
    enabled: !!nodeId,
    retry: false, // zero retries — readiness check to cosmetic badge, nie krytyczne
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async (): Promise<SchemaReadiness> => {
      try {
        if (!nodeId) {
          return { level: "loading", ...LEVELS.loading, reasons: [] };
        }

        // Fetch minimal metadata (maybeSingle — null gdy brak, nie throw)
        const { data: node } = await (supabase as any)
          .from("thematic_nodes")
          .select("title, slug, map_id")
          .eq("id", nodeId)
          .maybeSingle();
        if (!node?.title || !node?.slug || !node?.map_id) {
          return { level: "invalid", ...LEVELS.invalid, reasons: ["Brak node.title/slug/map_id"] };
        }

        const { data: map } = await (supabase as any)
          .from("thematic_maps")
          .select("website_id, active_profile_id")
          .eq("id", node.map_id)
          .maybeSingle();

        if (!map) {
          return { level: "invalid", ...LEVELS.invalid, reasons: ["Mapa nie znaleziona"] };
        }

        const website = map.website_id
          ? (await (supabase as any)
              .from("websites")
              .select("id, url, name, schema_enabled")
              .eq("id", map.website_id)
              .maybeSingle()).data
          : null;

        if (!website) {
          return { level: "invalid", ...LEVELS.invalid, reasons: ["Mapa nie ma website_id"] };
        }
        if (!website.url || !website.name) {
          return { level: "invalid", ...LEVELS.invalid, reasons: ["Website bez url/name"] };
        }

        if (website.schema_enabled === false || website.schema_enabled == null) {
          return {
            level: "off",
            ...LEVELS.off,
            reasons: ["website.schema_enabled = false (lub nie ustawione)"],
          };
        }

        // Schema ON — sprawdź kompletność (BEZ Organization — PBN footprint)
        const profile = map.active_profile_id
          ? (await (supabase as any)
              .from("thematic_writing_profiles")
              .select("public_author_label, author_slug, author_job_title, author_bio_html, author_photo_url, schema_enabled")
              .eq("id", map.active_profile_id)
              .maybeSingle()).data
          : null;

        const content = (await (supabase as any)
          .from("thematic_node_content")
          .select("meta_description")
          .eq("node_id", nodeId)
          .maybeSingle()).data;

        const reasons: string[] = [];
        let partial = false;

        // Person (per-profile toggle gate) — fallback minimal z website.name gdy brak
        if (!profile) {
          reasons.push("Person: brak active_profile (fallback z website.name)");
          partial = true;
        } else if (profile.schema_enabled !== true) {
          reasons.push("Person: wyłączona w profilu (toggle off) — fallback minimal");
          partial = true;
        } else if (!profile.public_author_label) {
          reasons.push("Person: brak public_author_label");
          partial = true;
        } else {
          if (!profile.author_slug) {
            reasons.push("Person: brak author_slug");
            partial = true;
          }
          if (!profile.author_job_title && !profile.author_bio_html) {
            reasons.push("Person: brak jobTitle i bio");
            partial = true;
          }
          if (!profile.author_photo_url) {
            reasons.push("Person: brak zdjęcia autora");
            partial = true;
          }
        }

        // BlogPosting
        if (!content?.meta_description) {
          reasons.push("BlogPosting: brak meta_description (fallback ze stripped HTML)");
          partial = true;
        }

        if (partial) {
          return { level: "partial", ...LEVELS.partial, reasons };
        }
        return { level: "ready", ...LEVELS.ready, reasons: [] };
      } catch (e) {
        // Silent fail — badge to kosmetyka, nie zatrzymuj UI
        const msg = e instanceof Error ? e.message : String(e);
        return {
          level: "invalid",
          ...LEVELS.invalid,
          reasons: [`Readiness check failed: ${msg}`],
        };
      }
    },
    staleTime: 60_000,
    gcTime: 300_000,
  });
}
